import React, { useCallback, useEffect, useState, useRef } from 'react';
import Loading from '../../../components/Loading';
import { useAppDispatch, useAppSelector } from '../../../hooks';
import { formatDate } from '../../../utils';
import { Alert, Button, Intent, Tooltip, IconName } from '@blueprintjs/core';
import Toaster from '../../../components/Toaster';
import { disableItem, enableItem, getAllUsers, setLoading } from '../reducer';
import { User, UsersState } from '../types';
import { UserContainer } from '../styles';
import SearchBar from '../components/searchBar/searchBar';
import Table from '../../../components/Table';
import { usePaginationSearchParams } from '../components/pagination/hooks';

const Users = () => {
  const { currentPage, setCurrentPage } = usePaginationSearchParams();
  const dispatch = useAppDispatch();
  const state: UsersState = useAppSelector((state) => state.user);

  const [isDisableDialogOpen, showDisableDialog] = useState(false);
  const [isActivateDialogOpen, showActiveDialog] = useState(false);
  const [disablingId, setDisablingId] = useState('');
  const [activatingId, setActivatingId] = useState('');
  const [itemsPerPage] = useState(10);
  const [filters, setFilters] = useState({
    isActive: true,
    name: '',
    roles: '',
    email: '',
  });

  useEffect(() => {
    dispatch(setLoading(true));
    dispatch(
      getAllUsers({ page: currentPage, limit: itemsPerPage, ...filters }),
    );
  }, [dispatch, currentPage, itemsPerPage, filters]);

  const showToasterMessage = useCallback(
    async (message: React.ReactChild, intent: Intent, icon: IconName) => {
      (await Toaster).show({
        message,
        intent,
        icon,
      });
    },
    [],
  );

  const totalPages = state.items?.totalPages ?? 0;

  const openDisablePopup = useCallback(
    (id: string) => {
      showDisableDialog(true);
      setDisablingId(id);
    },
    [showDisableDialog, setDisablingId],
  );

  const openActivatePopup = useCallback(
    (id: string) => {
      showActiveDialog(true);
      setActivatingId(id);
    },
    [showActiveDialog, setActivatingId],
  );

  const closeDisableDialog = useCallback(() => {
    showDisableDialog(false);
    setDisablingId('');
  }, [showDisableDialog, setDisablingId]);

  const closeActiveDialog = useCallback(() => {
    showActiveDialog(false);
    setActivatingId('');
  }, [showActiveDialog, setActivatingId]);

  const doDisableItem = useCallback(
    (id: string) => {
      dispatch(disableItem(id));
      dispatch(setLoading(true));
      dispatch(
        getAllUsers({ page: currentPage, limit: itemsPerPage, ...filters }),
      );
    },
    [dispatch, currentPage, itemsPerPage, filters],
  );

  const doEnableItem = useCallback(
    (id: string) => {
      dispatch(enableItem(id));
      dispatch(setLoading(true));
      dispatch(
        getAllUsers({ page: currentPage, limit: itemsPerPage, ...filters }),
      );
    },
    [dispatch, currentPage, itemsPerPage, filters],
  );

  const doDisableItemRef = useRef(doDisableItem);
  const doEnableItemRef = useRef(doEnableItem);

  useEffect(() => {
    doDisableItemRef.current = doDisableItem;
    doEnableItemRef.current = doEnableItem;
  }, [doDisableItem, doEnableItem]);

  const deactivateItem = useCallback(() => {
    showToasterMessage(
      <>
        Desativando usuário: <strong>{disablingId}</strong>
      </>,
      Intent.DANGER,
      'warning-sign',
    );
    doDisableItemRef.current(disablingId);
    closeDisableDialog();
  }, [disablingId, closeDisableDialog, showToasterMessage]);

  const activateItem = useCallback(() => {
    showToasterMessage(
      <>
        Ativando usuário: <strong>{activatingId}</strong>
      </>,
      Intent.WARNING,
      'warning-sign',
    );
    doEnableItemRef.current(activatingId);
    closeActiveDialog();
  }, [activatingId, closeActiveDialog, showToasterMessage]);

  const paginate = (pageNumber: number) => {
    if (pageNumber > totalPages) {
      setCurrentPage(1);
    } else {
      setCurrentPage(pageNumber);
    }
  };

  const handleFilterChange = (newFilters: Partial<typeof filters>) => {
    setCurrentPage(1);
    setFilters((prevFilters) => ({ ...prevFilters, ...newFilters }));
  };

  if (state.isLoading) {
    return <Loading message="Loading" />;
  }

  return (
    <UserContainer>
      <SearchBar
        isActive={filters.isActive}
        onFilterChange={handleFilterChange}
      />
      <div>
        <Table<User>
          rowKey="_id"
          data={state.items?.users || []}
          pagination={{
            currentPage,
            totalPages,
            onPageChange: paginate,
          }}
          columns={[
            {
              title: 'ID',
              render: (record) => record._id,
            },
            {
              title: 'Username',
              render: (record) => record.username,
            },
            {
              title: 'Email',
              render: (record) => record.email,
            },
            {
              title: 'Roles',
              render: (record) => record.roles.join(', '),
            },
            {
              title: 'Created',
              render: (record) => formatDate(record.createdAt.toString()),
            },
            {
              title: 'Last Modified',
              render: (record) => formatDate(record.updatedAt.toString()),
            },
            {
              title: 'Action',
              render: (record) => (
                <Tooltip
                  content={
                    <span>{record.isActive ? 'Desativar' : 'Ativar'}</span>
                  }
                  openOnTargetFocus={false}
                  usePortal={false}
                >
                  <Button
                    onClick={() =>
                      record.isActive
                        ? openDisablePopup(record._id)
                        : openActivatePopup(record._id)
                    }
                    icon={record.isActive ? 'trash' : 'tick'}
                    intent={record.isActive ? Intent.DANGER : Intent.SUCCESS}
                  />
                </Tooltip>
              ),
            },
          ]}
        />
      </div>

      <Alert
        isOpen={isDisableDialogOpen}
        onClose={closeDisableDialog}
        canEscapeKeyCancel
        canOutsideClickCancel
        icon="disable"
        intent={Intent.DANGER}
        cancelButtonText="Cancelar"
        confirmButtonText="Desativar"
        onCancel={closeDisableDialog}
        onConfirm={deactivateItem}
      >
        Tem certeza que deseja desativar este usuário?
      </Alert>

      <Alert
        isOpen={isActivateDialogOpen}
        onClose={closeActiveDialog}
        canEscapeKeyCancel
        canOutsideClickCancel
        icon="tick"
        intent={Intent.SUCCESS}
        cancelButtonText="Cancelar"
        confirmButtonText="Ativar"
        onCancel={closeActiveDialog}
        onConfirm={activateItem}
      >
        Tem certeza que deseja ativar este usuário?
      </Alert>
    </UserContainer>
  );
};

export default Users;
